// src/features/patient/care-treatment/components/OriginalViewer.tsx

/**
 * Purpose : The one way a Care & Treatment screen shows a record's original
 * file (the note page, the visit page, a call's row, To review's documents):
 * which file and pages, a switcher when the record has several sources, an
 * optional Close, and the existing file viewer opened at the record's first
 * page. A document opens as a document, a report as a report, everywhere.
 *
 * When the backend sends each source's upload, the "Open in Document Panel"
 * link belongs in this header, so every screen gets it at once.
 *
 * Contents :
 *
 * * function(OriginalViewer) : header, switcher, close, file viewer
 */

import { useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { UnderlineTabs } from "@/shared/components/UnderlineTabs";
import { cn } from "@/shared/lib/utils";
import { FilePane } from "@/features/patient/records/components/OriginalFileTab";
import type { Source } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { SOURCE_KIND_LABEL, pageRangeText } from "@/features/patient/care-treatment/utils/careTimeline";
import { cardClass, faintTextClass, outlineButtonClass, titleClass } from "./careStyles";

// function(OriginalViewer)
/**
 * @param source - The source to show.
 * @param actingAsProfileId - The dependent being viewed.
 * @param sources / index / onPick - All the record's sources and which is shown: a switcher when more than one.
 * @param onClose - Shows "Close original" when given.
 * @param scrollOnOpen - Bring the viewer into view when it opens (it may open above where the user clicked).
 * @param bare - No card of its own (inside another card).
 */
export function OriginalViewer({
  source, actingAsProfileId, sources, index = 0, onPick, onClose, scrollOnOpen = false, bare = false, className,
}: {
  source: Source;
  actingAsProfileId?: string;
  sources?: Source[];
  index?: number;
  onPick?: (index: number) => void;
  onClose?: () => void;
  scrollOnOpen?: boolean;
  bare?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const sourceKey = `${source.originalKind ?? "document"}:${source.originalRef}:${source.pageStart ?? ""}`;

  useEffect(() => {
    if (scrollOnOpen) ref.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [scrollOnOpen, sourceKey]);

  const shell = cn(!bare && cardClass, "min-w-0 scroll-mt-4 print:hidden", className);

  if (!source.originalRef) {
    return (
      <section ref={ref} aria-label="Original document" className={shell}>
        {/* The card variant: "compact" drops the icon, description and Close. */}
        <EmptyState
          className="py-8 shadow-none"
          icon={<Icon icon="lucide:file-x" className="h-10 w-10 text-[#9CA3AF]" />}
          title="Original file not available"
          description="We don't have a source file for this record."
          action={onClose && <Button variant="outline" onClick={onClose} className={outlineButtonClass}>Close</Button>}
        />
      </section>
    );
  }

  const switcher = sources && sources.length > 1 && onPick;
  const detail = [source.label && SOURCE_KIND_LABEL[source.kind], pageRangeText(source)].filter(Boolean).join(" · ");

  return (
    <section ref={ref} aria-label="Original document" className={shell}>
      {/* Which file this is and where the record sits in it, then the viewer below a line. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] pb-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF2F7]">
            <Icon icon={source.kind === "pdf" ? "lucide:file-text" : "lucide:file"} className="h-5 w-5 text-[#0A2947]" />
          </span>
          <div className="min-w-0">
            <p className={cn("!mb-0 truncate text-sm", titleClass)}>{source.label || SOURCE_KIND_LABEL[source.kind]}</p>
            {detail && <p className={cn("!mb-0", faintTextClass)}>{detail}</p>}
          </div>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose} className={outlineButtonClass}>Close original</Button>
        )}
      </div>
      {switcher && (
        <UnderlineTabs<string>
          label="Choose source"
          className="mb-4"
          active={String(index)}
          onChange={(i) => onPick(Number(i))}
          tabs={sources.map((s, i) => ({ id: String(i), label: s.label || SOURCE_KIND_LABEL[s.kind] }))}
        />
      )}
      <FilePane
        key={`${sourceKey}:${index}`}
        {...(source.originalKind === "report"
          ? { diagnosticReportId: source.originalRef }
          : { documentReferenceId: source.originalRef })}
        page={source.pageStart}
        actingAsProfileId={actingAsProfileId}
      />
    </section>
  );
}
